import { z } from "zod";
import { orderBy } from "lodash";
import { type AxiosResponse } from "axios";

import { packNetAxios } from "@/lib/api-client";
import {
	type AuditEvent,
	type AuditEventMinimal,
	auditEventMinimalSchema,
	auditEventSchema,
	type AuditEventType,
	type BatchAuditEntry,
	type BatchAuditEntryPatchRequest,
	batchAuditEntrySchema,
	type BatchAuditSearchRequest,
	type BatchAuditSearchResponse,
	batchAuditSearchResponseSchema,
	type BatchDeletionResponse,
	batchDeletionResponseSchema,
	type BatchRemovalResponse,
	batchRemovalResponseSchema,
	type CartonCountByProductionGroup,
	closedCartonsResponseSchema,
	type ConfigurationAuditHistory,
	type CountByCartonPropertyGroup,
	countByCartonPropertyGroupSchema,
	type CountByClassification,
	countByClassificationSchema,
	type CountByPickZone,
	countByPickZoneSchema,
	type EstimatedCorrugateUsageData,
	type LimitedProducibilityData,
	type MachineGroupSelectionHistory,
	type MachinesOutOfCorrugateData,
	type OpenCartonsCountByStatus,
	closingScanFailedResponseSchema,
} from "@/schemas";
import type { SelectionAuditResponse, SelectionAuditStat } from "@/schemas/audit/selection-analytics";
import type { Batch } from "@/schemas/routing/batch";

import { baseRoutes } from "./base-routes";

export const reproducePackagingSolution = (id: string) => {
	return packNetAxios.post(`${baseRoutes.audit}/v1/Reproduce/PackagingSolution/${id}`, null);
};

export const reproducePackagingSolutionByBatchId = (batchId: string) => {
	return packNetAxios.post<{ alias: string }>(
		`${baseRoutes.audit}/v1/Reproduce/PackagingSolution/byBatchId/${batchId}`,
		null,
	);
};

export async function getAuditCountsByStatus(): Promise<OpenCartonsCountByStatus> {
	const response = await packNetAxios.get<OpenCartonsCountByStatus>(
		`${baseRoutes.audit}/v1/CurrentState/PackagingSolutions/counts/byStatus`,
	);
	return response.data;
}

export async function getAuditCountsByProductionGroups(): Promise<CartonCountByProductionGroup[]> {
	const response = await packNetAxios.get<CartonCountByProductionGroup[]>(
		`${baseRoutes.audit}/v1/CurrentState/PackagingSolutions/counts/byProductionGroups`,
	);
	return response.data;
}

const countsByPickZoneSchema = z.array(countByPickZoneSchema);

export async function getAuditCountsByPickZones(): Promise<CountByPickZone[]> {
	const response = await packNetAxios.get(`${baseRoutes.audit}/v1/CurrentState/PackagingSolutions/counts/byPickzones`);

	return countsByPickZoneSchema.enrich({ callSite: "getAuditCountsByPickZones" }).parseWithTelemetry(response.data);
}

const countsByCartonPropertyGroupSchema = z.array(countByCartonPropertyGroupSchema);

export async function getAuditCountsByCartonPropertyGroups(): Promise<CountByCartonPropertyGroup[]> {
	const response = await packNetAxios.get(
		`${baseRoutes.audit}/v1/CurrentState/PackgingSolutions/counts/byCartonPropertyGroups`, // Typo in API
	);

	return countsByCartonPropertyGroupSchema
		.enrich({ callSite: "getAuditCountsByCartonPropertyGroups" })
		.parseWithTelemetry(response.data);
}

const countsByClassificationSchema = z.array(countByClassificationSchema);

export async function getAuditCountsByClassifications(): Promise<CountByClassification[]> {
	const response = await packNetAxios.get(
		`${baseRoutes.audit}/v1/CurrentState/PackgingSolutions/counts/byClassifications`, // Typo in API
	);

	return countsByClassificationSchema
		.enrich({ callSite: "getAuditCountsByClassifications" })
		.parseWithTelemetry(response.data);
}

export async function getMachinesOutOfCorrugate(): Promise<MachinesOutOfCorrugateData> {
	const response = await packNetAxios.get<MachinesOutOfCorrugateData>(
		`${baseRoutes.audit}/v1/CurrentState/machinesOutOfCorrugate`,
	);
	return response.data;
}

export async function getLimitedProducibility(): Promise<LimitedProducibilityData> {
	const response = await packNetAxios.get<LimitedProducibilityData>(
		`${baseRoutes.audit}/v1/CurrentState/limitedProducibility/counts`,
	);
	return response.data;
}

export async function getEstimatedCorrugateUsage(): Promise<EstimatedCorrugateUsageData> {
	const response = await packNetAxios.get<EstimatedCorrugateUsageData>(
		`${baseRoutes.audit}/v1/CurrentState/PackagingSolutions/byStatus/Open/estimatedCorrugateUsage`,
	);
	return response.data;
}

export async function getMachineGroupHistory(id: string): Promise<ConfigurationAuditHistory> {
	const response = await packNetAxios.get(`${baseRoutes.audit}/v1/ConfigurationAudit/MachineGroup/${id}`);
	// const parsedData = configurationAuditHistorySchema.parse(response.data);
	return response.data;
}

export async function getProductionGroupHistory(id: string): Promise<ConfigurationAuditHistory> {
	const response = await packNetAxios.get(`${baseRoutes.audit}/v1/ConfigurationAudit/ProductionGroup/${id}`);
	// const parsedData = configurationAuditHistorySchema.parse(response.data);
	return response.data;
}

export interface BatchHistoryItem extends Batch {
	batchId: string;
	statusLastUpdatedUTC: string;
}

interface BatchHistoryResponse extends Batch {
	id: string;
}

export const getBatchHistory = async (machineGroupId: string): Promise<BatchHistoryItem[]> => {
	const response = await packNetAxios.get<BatchHistoryResponse[]>(
		`${baseRoutes.audit}/v1/Batch/unifiedHistory/byMachineGroup/${machineGroupId}/limit/100`,
	);

	return response.data.map((b) => ({
		...b,
		batchId: b.id,
		statusLastUpdatedUTC: b.lastUpdatedUTC,
	}));
};

export const getBatchAuditSearch = async ({
	pagination,
	dateRange,
	...base
}: BatchAuditSearchRequest): Promise<BatchAuditSearchResponse> => {
	const searchParams = new URLSearchParams();

	Object.entries({ ...base, ...pagination, ...dateRange }).forEach(([key, value]) => {
		if (["undefined", "null", ""].includes(`${value}`)) {
			return;
		}

		searchParams.append(key, `${value}`);
	});

	const response = await packNetAxios.get(`${baseRoutes.audit}/v1/Batch/?${searchParams.toString()}`);

	const batchAuditResponse = batchAuditSearchResponseSchema
		.enrich({ callSite: "getBatchAuditSearch" })
		.parseWithTelemetry(response.data);

	return {
		...batchAuditResponse,
		entries: orderBy(batchAuditResponse.entries, (b) => b.updatedTimestamp, "desc"),
	};
};

export const getBatchAuditEntry = async (batchId: string): Promise<BatchAuditEntry> => {
	const response = await packNetAxios.get(`${baseRoutes.audit}/v1/Batch/${batchId}`);

	return batchAuditEntrySchema.enrich({ callSite: "getBatchAuditEntry" }).parseWithTelemetry(response.data);
};

const batchAuditEventsSchema = z.array(auditEventSchema);

export const getBatchAuditEvents = async (batchId: string, auditTypes: AuditEventType[]): Promise<AuditEvent[]> => {
	const response = await searchBatchAuditEvents(batchId, auditTypes, new URLSearchParams());

	const batchAuditEntries = batchAuditEventsSchema
		.enrich({ callSite: "getBatchAuditEvents" })
		.parseWithTelemetry(response.data);

	return orderBy(batchAuditEntries, (e) => e.createDateUTC, "desc");
};

const batchAuditEventMinimalSchema = z.array(auditEventMinimalSchema);
export const getBatchAuditEventsMinimal = async (
	batchId: string,
	auditTypes: AuditEventType[],
): Promise<AuditEventMinimal[]> => {
	const searchParams = new URLSearchParams();

	searchParams.append("returnFullEntries", "false");

	const response = await searchBatchAuditEvents(batchId, auditTypes, searchParams);

	const batchAuditEntries = batchAuditEventMinimalSchema
		.enrich({ callSite: "getBatchAuditEventsMinimal" })
		.parseWithTelemetry(response.data);

	return orderBy(batchAuditEntries, (e) => e.createDateUTC, "desc");
};

const searchBatchAuditEvents = (
	batchId: string,
	auditTypes: AuditEventType[],
	searchParams: URLSearchParams,
): Promise<AxiosResponse<unknown>> => {
	auditTypes.forEach((type) => {
		searchParams.append("auditTypes", type);
	});

	return packNetAxios.get(`${baseRoutes.audit}/v1/Audit/Batches/${batchId}?${searchParams.toString()}`);
};

export const getPackagingSolutionAuditEvents = async (
	packagingSolutionId: string,
	auditTypes: AuditEventType[],
): Promise<AuditEvent[]> => {
	// Validate packagingSolutionId is a valid UUID format
	const validation = z
		.uuid()
		.enrich({ callSite: "getPackagingSolutionAuditEvents" })
		.safeParseWithTelemetry(packagingSolutionId);
	if (!validation.success) {
		throw new Error(`Invalid packaging solution ID format: ${packagingSolutionId}`);
	}

	const searchParams = new URLSearchParams();

	auditTypes.forEach((type) => {
		searchParams.append("auditTypes", type);
	});

	const response = await packNetAxios.get(
		`${baseRoutes.audit}/v1/Audit/PackagingSolutions/${validation.data}?${searchParams.toString()}`,
	);

	const batchAuditEntries = batchAuditEventsSchema
		.enrich({ callSite: "getPackagingSolutionAuditEvents" })
		.parseWithTelemetry(response.data);

	return orderBy(batchAuditEntries, (e) => e.createDateUTC, "desc");
};

export const removeBatchAuditEntry = async (batchId: string) => {
	await packNetAxios.put(`${baseRoutes.audit}/v1/Batch/${batchId}/Remove`, {});
};

export const removeBatchAuditEntries = async (batchIds: string[]): Promise<BatchRemovalResponse> => {
	const response = await packNetAxios.put(`${baseRoutes.audit}/v1/Batch/Remove`, batchIds);

	return batchRemovalResponseSchema.enrich({ callSite: "removeBatchAuditEntries" }).parseWithTelemetry(response.data);
};

export const deleteBatchAuditEntries = async (batchIds: string[]): Promise<BatchDeletionResponse> => {
	const response = await packNetAxios.delete(`${baseRoutes.audit}/v1/Batch/delete`, { data: batchIds });

	return batchDeletionResponseSchema.enrich({ callSite: "deleteBatchAuditEntries" }).parseWithTelemetry(response.data);
};

export const cloneBatchAuditEntry = async (batchId: string) => {
	await packNetAxios.post(`${baseRoutes.audit}/v1/Batch/${batchId}/Clone`, {});
};

export const patchBatchAuditEntry = async (batchId: string, data: BatchAuditEntryPatchRequest) => {
	await packNetAxios.patch(`${baseRoutes.audit}/v1/Batch/${batchId}`, data);
};

export const getAuditEvent = async (auditEventId: string) => {
	// Validate auditEventId is a valid UUID format
	const validation = z.uuid().enrich({ callSite: "getAuditEvent" }).safeParseWithTelemetry(auditEventId);
	if (!validation.success) {
		throw new Error(`Invalid audit event ID format: ${auditEventId}`);
	}

	const response = await packNetAxios.get(`${baseRoutes.audit}/v1/Audit/${validation.data}`);

	return auditEventSchema.enrich({ callSite: "getAuditEvent" }).parseWithTelemetry(response.data);
};

export async function getSelectionHistory(): Promise<MachineGroupSelectionHistory[]> {
	const response = await packNetAxios.get<MachineGroupSelectionHistory[]>(
		`${baseRoutes.audit}/v1/SelectionHistory/history`,
	);
	return response.data;
}

export async function getSelectionAuditStats(): Promise<SelectionAuditStat[]> {
	const response = await packNetAxios.get<SelectionAuditResponse>(`${baseRoutes.audit}/v1/Analytics/selection`);
	return response.data?.selectionHistoryForProductionGroups ?? [];
}

interface GetClosedCartonsParams {
	days?: number;
	pageNumber: number;
	pageSize: number;
	filter: string;
}

export async function getClosedCartons({ days = 7, pageNumber, pageSize, filter = "" }: GetClosedCartonsParams) {
	const searchParams = new URLSearchParams({
		daysBefore: String(days),
		pageNumber: String(pageNumber),
		pageSize: String(pageSize),
		filter,
	});

	const response = await packNetAxios.get(`${baseRoutes.audit}/v1/closing/closinghistory/?${searchParams.toString()}`);
	return closedCartonsResponseSchema.enrich({ callSite: "getClosedCartons" }).parseWithTelemetry(response.data);
}

export interface GetClosingScanFailedParams {
	pageNumber: number;
	pageSize: number;
}

export async function getClosingScanFailed({ pageNumber, pageSize }: GetClosingScanFailedParams) {
	const pageParams = new URLSearchParams({
		pageNumber: String(pageNumber),
		pageSize: String(pageSize),
	});

	const response = await packNetAxios.get(`${baseRoutes.audit}/v1/closing/failedScans?${pageParams.toString()}`);
	return closingScanFailedResponseSchema.enrich({ callSite: "getClosingScanFailed" }).parseWithTelemetry(response.data);
}

export async function getVersion(): Promise<string> {
	const response = await packNetAxios.get<string>(`${baseRoutes.audit}/v1/Audit/version`);
	return response.data;
}
